import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useOrganization } from '../context/OrganizationContext';
import { usePersistedPreference } from '../hooks/useFilterPersistence';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/auth';
import {
  OrganizationOKR,
  KeyResult,
  OKRUpdate,
  KeyResultCreate,
  okrService,
} from '../services/okrs';
import { OKRScoreDisplay } from '../components/okr/OKRScoreDisplay';
import { ManualScoreEditor } from '../components/okr/ManualScoreEditor';
import { OwnerSelector } from '../components/okr/OwnerSelector';
import { AddRelationshipModal } from '../components/okr/AddRelationshipModal';
import { formatCommitmentType } from '../utils/enumFormatting';
import { OKR_COMMITMENT_TYPES } from '../constants/enums';
import {
  PlanningCycle,
  PlanningConfiguration,
  planningCyclesApi,
  planningConfigApi,
} from '../services/planningCycles';
import PlanningCycleSelector from '../components/PlanningCycleSelector';
import { okrRelationshipsService } from '../services/okrRelationships';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import TaskDetailPanel from '../components/TaskDetailPanel';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import ResearchTab from '../components/ResearchTab';
import { ResearchEntityType } from '../services/researchService';
import TaskModal from '../components/TaskModal';
import { API_URL } from '../constants/urls';

const OKRDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, canEdit } = useOrganization();
  const { user } = useAuth();

  // Function to get initial tab from URL hash
  const getInitialTab = (): 'overview' | 'tasks' | 'research' => {
    const hash = location.hash.replace('#', '');
    if (hash === 'tasks' || hash === 'research') {
      return hash;
    }
    return 'overview';
  };

  // State management following SegmentDetail pattern
  const [okr, setOkr] = useState<OrganizationOKR | null>(null);
  const [editForm, setEditForm] = useState<Partial<OKRUpdate>>({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<any[]>([]);
  const [areas, setAreas] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([]);
  const [planningCycles, setPlanningCycles] = useState<PlanningCycle[]>([]);
  const [planningConfig, setPlanningConfig] =
    useState<PlanningConfiguration | null>(null);

  // State for tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'research'>(
    getInitialTab(),
  );
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [taskViewMode, setTaskViewMode] = usePersistedPreference<
    'kanban' | 'list'
  >('okrs.taskView', 'kanban', 'ui');
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [allOKRs, setAllOKRs] = useState<OrganizationOKR[]>([]);

  // Key Results edit state
  const [keyResultsEdit, setKeyResultsEdit] = useState<KeyResult[]>([]);
  const [newKeyResults, setNewKeyResults] = useState<KeyResultCreate[]>([]);
  const [deletedKeyResultIds, setDeletedKeyResultIds] = useState<string[]>([]);

  // Individual KR editing state
  const [editingKrId, setEditingKrId] = useState<string | null>(null);
  const [editingKrData, setEditingKrData] = useState<Partial<KeyResult> | null>(
    null,
  );
  const [savingKrId, setSavingKrId] = useState<string | null>(null);
  const [showNewKrForm, setShowNewKrForm] = useState(false);
  const [newKrData, setNewKrData] = useState<KeyResultCreate>({
    description: '',
    target_value: 100,
    unit: '%',
    commitment_type: 'committed',
  });

  // Parent OKR management state
  const [parentOkr, setParentOkr] = useState<OrganizationOKR | null>(null);
  const [showParentSelector, setShowParentSelector] = useState(false);
  const [availableParents, setAvailableParents] = useState<OrganizationOKR[]>(
    [],
  );

  // Child OKRs display state
  const [showAllChildren, setShowAllChildren] = useState(false);

  // Relationship management state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);

  // Tasks for Key Results
  const [keyResultTasks, setKeyResultTasks] = useState<{
    [krId: string]: MasterPlanTask[];
  }>({});
  const [loadingTasks, setLoadingTasks] = useState<{ [krId: string]: boolean }>(
    {},
  );
  const [expandedKrTasks, setExpandedKrTasks] = useState<{
    [krId: string]: boolean;
  }>({});
  const [selectedTask, setSelectedTask] = useState<MasterPlanTask | null>(null);
  const [showTaskDetailPanel, setShowTaskDetailPanel] = useState(false);

  // Task creation modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedKrId, setSelectedKrId] = useState<string | null>(null);

  // Load OKR data
  // Load tasks for a specific Key Result - moved here to fix initialization error
  const loadTasksForKeyResult = async (krId: string) => {
    if (!selectedOrg) return;

    setLoadingTasks((prev) => ({ ...prev, [krId]: true }));

    try {
      const response = await axios.get(`${API_URL}/master-plan/tasks`, {
        params: {
          organization_id: selectedOrg.id,
          key_result_id: krId,
        },
        headers: getAuthHeaders(),
      });
      setKeyResultTasks((prev) => ({ ...prev, [krId]: response.data }));
    } catch (error) {
      console.error('Failed to load tasks for key result:', error);
    } finally {
      setLoadingTasks((prev) => ({ ...prev, [krId]: false }));
    }
  };

  // Load areas for dropdown
  const loadAreas = async () => {
    if (!selectedOrg) return;
    try {
      const response = await axios.get(
        `${API_URL}/areas?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setAreas(response.data || []);
    } catch (error) {
      console.error('Failed to load areas:', error);
    }
  };

  const loadOKR = async () => {
    if (!id || !selectedOrg) return;

    try {
      setLoading(true);
      const data = await okrService.getOKR(id, selectedOrg.id);
      setOkr(data);

      // Load tasks for each key result
      if (data.key_results) {
        data.key_results.forEach((kr) => {
          loadTasksForKeyResult(kr.id);
        });
      }

      // Initialize edit form with current values
      setEditForm({
        objective: data.objective,
        description: data.description,
        owner_id: data.owner?.id,
        area_id: data.area_id,
        status: data.status,
        commitment_type: data.commitment_type,
        auto_score: data.auto_score,
        score: data.score,
        score_justification: data.score_justification,
        is_corporate: data.is_corporate,
        planning_cycle_id: data.planning_cycle_id,
      });

      // Initialize key results for editing
      setKeyResultsEdit(data.key_results || []);

      // Load parent OKR if it exists
      if (data.parent_okr_id) {
        try {
          const parent = await okrService.getOKR(
            data.parent_okr_id,
            selectedOrg.id,
          );
          setParentOkr(parent);
        } catch (err) {
          console.error('Failed to load parent OKR:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load OKR:', error);
      alert('Failed to load OKR');
      navigate('/okrs'); // Changed from /objectives to /okrs
    } finally {
      setLoading(false);
    }
  };

  // Load organization members for owner selection
  const loadMembers = async () => {
    if (!selectedOrg) return;

    try {
      const response = await axios.get(
        `${API_URL}/organizations/${selectedOrg.id}/members`,
        { headers: getAuthHeaders() },
      );
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  // Load planning cycles and config
  const loadPlanningCycles = async () => {
    if (!selectedOrg) return;

    try {
      const [cycles, config] = await Promise.all([
        planningCyclesApi.listCycles(selectedOrg.id),
        planningConfigApi.getConfig(selectedOrg.id),
      ]);
      setPlanningCycles(cycles);
      setPlanningConfig(config);
    } catch (error) {
      console.error('Failed to load planning cycles:', error);
    }
  };

  // Tab navigation handler
  const handleTabChange = (tab: 'overview' | 'tasks' | 'research') => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  // Fetch tasks for this OKR (including child OKRs)
  const fetchTasks = async (orgId: string) => {
    if (!id) return;
    try {
      const fetchedTasks = await masterPlanTaskService.list(orgId, {
        okr_ids: [id],
        include_org_tasks: false, // Only show OKR-specific tasks
      });
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Fetch OKRs for task alignment
  const fetchOKRs = async (orgId: string) => {
    try {
      const okrs = await okrService.listOKRs(orgId);
      // Filter for only draft and active OKRs
      let activeOKRs = okrs.filter(
        (okr) => okr.status === 'draft' || okr.status === 'active',
      );

      // Always include the current OKR being viewed, regardless of status
      // This allows creating tasks aligned to completed/cancelled OKRs
      if (okr && !activeOKRs.some((o) => o.id === okr.id)) {
        activeOKRs = [okr, ...activeOKRs];
      }

      setAllOKRs(activeOKRs);
    } catch (error) {
      console.error('Error fetching OKRs:', error);
    }
  };

  // Fetch organization members for task assignment
  const fetchOrganizationMembers = async (orgId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/organizations/${orgId}/members`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setOrganizationMembers(data);
      }
    } catch (error) {
      console.error('Error fetching organization members:', error);
    }
  };

  // Task CRUD handlers
  const handleTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    if (!selectedOrg) return;
    try {
      // Find the task to get its key_result_id
      const taskToUpdate = tasks.find((t) => t.id === taskId);

      await masterPlanTaskService.update(selectedOrg.id, taskId, updates);

      // Refresh Tasks tab
      await fetchTasks(selectedOrg.id);

      // If task has a key result, also refresh that KR's tasks for Overview tab
      if (taskToUpdate?.key_result_id) {
        await loadTasksForKeyResult(taskToUpdate.key_result_id);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskCreate = async (createdTask?: MasterPlanTask) => {
    // TaskModal already creates the task, just refresh the list
    if (!selectedOrg) return;
    await fetchTasks(selectedOrg.id);

    // If task has a key result, reload that KR's tasks for Overview tab
    if (createdTask?.key_result_id) {
      const krId = createdTask.key_result_id;

      // Reload the tasks for this specific Key Result
      await loadTasksForKeyResult(krId);

      // Auto-expand the tasks section if it was collapsed
      setExpandedKrTasks((prev) => ({
        ...prev,
        [krId]: true,
      }));
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.delete(selectedOrg.id, taskId);
      await fetchTasks(selectedOrg.id);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  useEffect(() => {
    loadOKR();
    loadMembers();
    loadPlanningCycles();
    loadAreas();

    // Load additional data for tasks tab
    if (selectedOrg) {
      fetchTasks(selectedOrg.id);
      fetchOKRs(selectedOrg.id);
      fetchOrganizationMembers(selectedOrg.id);
    }
  }, [id, selectedOrg]);

  // Handle hash changes for tab navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'tasks' || hash === 'research') {
        setActiveTab(hash);
      } else {
        setActiveTab('overview');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Enter edit mode
  const handleEdit = () => {
    setEditMode(true);
    setKeyResultsEdit([...(okr?.key_results || [])]);
    setNewKeyResults([]);
    setDeletedKeyResultIds([]);
  };

  // Cancel edit mode
  const handleCancel = () => {
    setEditMode(false);
    // Reset form to original values
    if (okr) {
      setEditForm({
        objective: okr.objective,
        description: okr.description,
        owner_id: okr.owner?.id,
        status: okr.status,
        commitment_type: okr.commitment_type,
        auto_score: okr.auto_score,
        score: okr.score,
        score_justification: okr.score_justification,
        is_corporate: okr.is_corporate,
        planning_cycle_id: okr.planning_cycle_id,
      });
      setKeyResultsEdit(okr.key_results || []);
    }
    setNewKeyResults([]);
    setDeletedKeyResultIds([]);
  };

  // Save changes
  const handleSave = async () => {
    if (!okr || !selectedOrg) return;

    setSaving(true);
    try {
      // Update OKR basic info
      await okrService.updateOKR(okr.id, selectedOrg.id, editForm);

      // Handle Key Results updates
      // Update existing KRs
      for (const kr of keyResultsEdit) {
        if (!deletedKeyResultIds.includes(kr.id)) {
          await okrService.updateKeyResult(okr.id, kr.id, selectedOrg.id, {
            description: kr.description,
            target_value: kr.target_value,
            current_value: kr.current_value,
            unit: kr.unit,
            status: kr.status,
            owner_id: kr.owner_id,
          });
        }
      }

      // Delete removed KRs
      for (const krId of deletedKeyResultIds) {
        await okrService.deleteKeyResult(okr.id, krId, selectedOrg.id);
      }

      // Add new KRs
      for (const newKr of newKeyResults) {
        await okrService.addKeyResult(okr.id, selectedOrg.id, newKr);
      }

      // Successfully updated
      setEditMode(false);
      await loadOKR(); // Reload to get fresh data
    } catch (error) {
      console.error('Failed to save OKR:', error);
      alert('Failed to save OKR');
    } finally {
      setSaving(false);
    }
  };

  // Add new Key Result
  const handleAddKeyResult = () => {
    const newKr: KeyResultCreate = {
      description: '',
      target_value: 100,
      unit: '',
    };
    setNewKeyResults([...newKeyResults, newKr]);
  };

  // Update existing Key Result
  const handleUpdateKeyResult = (
    index: number,
    updates: Partial<KeyResult>,
  ) => {
    const updated = [...keyResultsEdit];
    updated[index] = { ...updated[index], ...updates };
    setKeyResultsEdit(updated);
  };

  // Update new Key Result
  const handleUpdateNewKeyResult = (
    index: number,
    updates: Partial<KeyResultCreate>,
  ) => {
    const updated = [...newKeyResults];
    updated[index] = { ...updated[index], ...updates };
    setNewKeyResults(updated);
  };

  // Remove Key Result
  const handleRemoveKeyResult = (kr: KeyResult) => {
    setDeletedKeyResultIds([...deletedKeyResultIds, kr.id]);
    setKeyResultsEdit(keyResultsEdit.filter((k) => k.id !== kr.id));
  };

  // Remove new Key Result
  const handleRemoveNewKeyResult = (index: number) => {
    setNewKeyResults(newKeyResults.filter((_, i) => i !== index));
  };

  // Create child OKR
  // Create related OKR from KR
  const handleCreateRelatedOKR = (kr: KeyResult) => {
    // Navigate to objectives list with create param to open modal
    // Pass KR details for auto-naming the new OKR
    const krDescription = encodeURIComponent(kr.description);

    // Use current user (person creating the OKR), not the KR owner
    const currentUserName = user
      ? encodeURIComponent(
          `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
            user.email,
        )
      : '';

    navigate(
      `/okrs?create=true&related_kr=${kr.id}&parent_id=${okr?.id}&kr_desc=${krDescription}&current_user=${currentUserName}`,
    );
  };

  // Individual KR editing functions
  const handleEditKr = (kr: KeyResult) => {
    setEditingKrId(kr.id);
    setEditingKrData({
      ...kr,
      commitment_type: kr.commitment_type || 'committed',
    });
  };

  const handleCancelKrEdit = () => {
    setEditingKrId(null);
    setEditingKrData(null);
  };

  const handleSaveKr = async () => {
    if (!editingKrId || !editingKrData || !okr || !selectedOrg) return;

    setSavingKrId(editingKrId);
    try {
      await okrService.updateKeyResult(okr.id, editingKrId, selectedOrg.id, {
        description: editingKrData.description,
        target_value: editingKrData.target_value,
        current_value: editingKrData.current_value,
        unit: editingKrData.unit,
        status: editingKrData.status,
        owner_id: editingKrData.owner_id,
        commitment_type: editingKrData.commitment_type,
      });

      await loadOKR(); // Reload to get fresh data
      setEditingKrId(null);
      setEditingKrData(null);
    } catch (error) {
      console.error('Failed to save key result:', error);
      alert('Failed to save key result');
    } finally {
      setSavingKrId(null);
    }
  };

  const handleDeleteKr = async (krId: string) => {
    if (!okr || !selectedOrg) return;

    if (window.confirm('Are you sure you want to delete this key result?')) {
      try {
        await okrService.deleteKeyResult(okr.id, krId, selectedOrg.id);
        await loadOKR();
      } catch (error) {
        console.error('Failed to delete key result:', error);
        alert('Failed to delete key result');
      }
    }
  };

  const handleAddNewKr = async () => {
    if (!okr || !selectedOrg || !newKrData.description || !newKrData.unit) {
      alert('Please fill in all required fields');
      return;
    }

    setSavingKrId('new');
    try {
      await okrService.addKeyResult(okr.id, selectedOrg.id, newKrData);

      // Reset form and reload
      setShowNewKrForm(false);
      setNewKrData({
        description: '',
        target_value: 100,
        unit: '%',
        commitment_type: 'committed',
      });
      await loadOKR();
    } catch (error) {
      console.error('Failed to add key result:', error);
      alert('Failed to add key result');
    } finally {
      setSavingKrId(null);
    }
  };

  // Delete OKR
  const handleDelete = async () => {
    if (!okr || !selectedOrg) return;

    if (
      window.confirm(
        'Are you sure you want to delete this OKR? This action cannot be undone.',
      )
    ) {
      try {
        // Try normal deletion first
        const result = await okrService.deleteOKR(
          okr.id,
          selectedOrg.id,
          false,
        );

        // Show cleanup summary if available
        if (result.cleanup_summary) {
          const summary = result.cleanup_summary;
          const messages = [];
          if (summary.child_okrs_unlinked > 0) {
            messages.push(
              `${summary.child_okrs_unlinked} child OKR(s) unlinked`,
            );
          }
          if (summary.tasks_unlinked > 0) {
            messages.push(`${summary.tasks_unlinked} task(s) unlinked`);
          }
          if (summary.key_results_deleted > 0) {
            messages.push(
              `${summary.key_results_deleted} key result(s) deleted`,
            );
          }
          if (messages.length > 0) {
            alert(`OKR deleted successfully. Cleanup: ${messages.join(', ')}`);
          }
        }

        navigate('/okrs');
      } catch (error: any) {
        const errorDetail = error.response?.data?.detail;

        // Check if it's a relationship error that can be forced
        if (errorDetail && errorDetail.includes('Use force=true')) {
          const forceDelete = window.confirm(
            `${errorDetail}\n\nDo you want to force delete and unlink all relationships?`,
          );

          if (forceDelete) {
            try {
              const result = await okrService.deleteOKR(
                okr.id,
                selectedOrg.id,
                true,
              );

              // Show what was cleaned up
              if (result.cleanup_summary) {
                const summary = result.cleanup_summary;
                const messages = [];
                if (summary.child_okrs_unlinked > 0) {
                  messages.push(
                    `${summary.child_okrs_unlinked} child OKR(s) unlinked`,
                  );
                }
                if (summary.tasks_unlinked > 0) {
                  messages.push(`${summary.tasks_unlinked} task(s) unlinked`);
                }
                if (summary.key_results_deleted > 0) {
                  messages.push(
                    `${summary.key_results_deleted} key result(s) deleted`,
                  );
                }
                alert(
                  `OKR deleted successfully. Cleanup: ${messages.join(', ')}`,
                );
              } else {
                alert('OKR deleted successfully');
              }

              navigate('/okrs');
            } catch (forceError) {
              console.error('Failed to force delete OKR:', forceError);
              alert(
                'Failed to delete OKR even with force. Please contact support.',
              );
            }
          }
        } else {
          console.error('Failed to delete OKR:', error);
          alert(errorDetail || 'Failed to delete OKR');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!okr) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">OKR not found</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getKRStatusColor = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 bg-green-100';
      case 'at_risk':
        return 'text-yellow-600 bg-yellow-100';
      case 'behind':
        return 'text-red-600 bg-red-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const toggleTasksExpansion = (krId: string) => {
    setExpandedKrTasks((prev) => ({ ...prev, [krId]: !prev[krId] }));
    // Load tasks if not already loaded
    if (!keyResultTasks[krId] && !loadingTasks[krId]) {
      loadTasksForKeyResult(krId);
    }
  };

  const handleTaskClick = (task: MasterPlanTask) => {
    setSelectedTask(task);
    setShowTaskDetailPanel(true);
  };

  const handleKeyResultTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    // Update the task in the local state
    setKeyResultTasks((prev) => {
      const newTasks = { ...prev };
      Object.keys(newTasks).forEach((krId) => {
        newTasks[krId] = newTasks[krId].map((task) =>
          task.id === taskId ? { ...task, ...updates } : task,
        );
      });
      return newTasks;
    });
  };

  // Handle task creation for a specific Key Result
  const handleKrTaskCreate = async (createdTask: MasterPlanTask) => {
    if (!selectedOrg) return;

    // Reload tasks for the specific Key Result in Overview tab
    if (createdTask.key_result_id) {
      const krId = createdTask.key_result_id;

      // Reload the tasks for this specific Key Result
      await loadTasksForKeyResult(krId);

      // Auto-expand the tasks section if it was collapsed
      setExpandedKrTasks((prev) => ({
        ...prev,
        [krId]: true,
      }));
    }

    // ALWAYS refresh the main tasks list (for Tasks tab)
    await fetchTasks(selectedOrg.id);

    setShowTaskModal(false);
    setSelectedKrId(null);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header with breadcrumb */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/okrs')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Objectives
        </button>

        <div className="flex justify-between items-start">
          <div className="flex-1">
            {editMode ? (
              <input
                type="text"
                value={editForm.objective}
                onChange={(e) =>
                  setEditForm({ ...editForm, objective: e.target.value })
                }
                className="text-2xl font-bold w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Objective title"
              />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900">
                {okr.formatted_reference && `${okr.formatted_reference} - `}
                {okr.objective}
              </h1>
            )}

            <div className="flex items-center gap-4 mt-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(okr.status)}`}
              >
                {okr.status.charAt(0).toUpperCase() + okr.status.slice(1)}
              </span>

              {okr.is_corporate && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Corporate
                </span>
              )}

              <span className="text-sm text-gray-500">
                {formatCommitmentType(okr.commitment_type)}
              </span>

              {okr.owner && (
                <div className="flex items-center text-sm text-gray-600">
                  <UserCircleIcon className="h-4 w-4 mr-1" />
                  {`${okr.owner.first_name || ''} ${okr.owner.last_name || ''}`.trim() ||
                    okr.owner.email}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {editMode ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                  )}
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <>
                    <button
                      onClick={handleEdit}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <nav className="flex space-x-4 border-b border-gray-200 mb-6">
        <button
          onClick={() => handleTabChange('overview')}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeTab === 'overview'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => handleTabChange('tasks')}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeTab === 'tasks'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Tasks
        </button>
        <button
          onClick={() => handleTabChange('research')}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeTab === 'research'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Research
        </button>
      </nav>
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Overview</h2>

              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Add a description..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={editForm.status}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            status: e.target.value as any,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={editForm.commitment_type}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            commitment_type: e.target.value as
                              | 'committed'
                              | 'aspirational',
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {OKR_COMMITMENT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Area
                    </label>
                    <select
                      value={editForm.area_id || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          area_id: e.target.value || undefined,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">No specific area</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner
                    </label>
                    <select
                      value={editForm.owner_id || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          owner_id: e.target.value || undefined,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Unassigned</option>
                      {members.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name || member.user_email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {planningConfig && planningCycles.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Planning Cycle
                      </label>
                      <PlanningCycleSelector
                        organizationId={selectedOrg?.id || ''}
                        currentCycleId={editForm.planning_cycle_id || ''}
                        cycles={planningCycles}
                        configuredCycleTypes={planningConfig.cycle_types || []}
                        onCycleSelect={(cycleId) =>
                          setEditForm({
                            ...editForm,
                            planning_cycle_id: cycleId || undefined,
                          })
                        }
                        onCyclesUpdate={(updatedCycles) =>
                          setPlanningCycles(updatedCycles)
                        }
                      />
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_corporate"
                      checked={editForm.is_corporate || false}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          is_corporate: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <label
                      htmlFor="is_corporate"
                      className="text-sm text-gray-700"
                    >
                      Corporate Objective
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-700">
                    {okr.description || 'No description provided'}
                  </p>

                  {okr.parent_okr && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center text-sm">
                        <RocketLaunchIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-600">Parent Objective:</span>
                        <button
                          onClick={() =>
                            navigate(`/okrs/${okr.parent_okr?.id}`)
                          }
                          className="ml-2 font-medium text-blue-600 hover:text-blue-800"
                        >
                          {(okr.parent_okr as any).formatted_reference &&
                            `${(okr.parent_okr as any).formatted_reference} - `}
                          {okr.parent_okr.objective}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Key Results */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Key Results ({okr.key_results.length})
                </h2>
                {canEdit && !editMode && (
                  <button
                    onClick={() => setShowNewKrForm(true)}
                    className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Key Result
                  </button>
                )}
                {editMode && (
                  <button
                    onClick={handleAddKeyResult}
                    className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Key Result
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {/* New Key Result Form (when not in full edit mode) */}
                {showNewKrForm && !editMode && (
                  <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200 border-dashed">
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newKrData.description}
                          onChange={(e) =>
                            setNewKrData({
                              ...newKrData,
                              description: e.target.value,
                            })
                          }
                          className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                          placeholder="Key result description"
                          autoFocus
                        />
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600">
                            Target
                          </label>
                          <input
                            type="number"
                            value={newKrData.target_value}
                            onChange={(e) =>
                              setNewKrData({
                                ...newKrData,
                                target_value: Number(e.target.value),
                              })
                            }
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Unit
                          </label>
                          <input
                            type="text"
                            value={newKrData.unit}
                            onChange={(e) =>
                              setNewKrData({
                                ...newKrData,
                                unit: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="%"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Type
                          </label>
                          <select
                            value={newKrData.commitment_type}
                            onChange={(e) =>
                              setNewKrData({
                                ...newKrData,
                                commitment_type: e.target.value as
                                  | 'committed'
                                  | 'aspirational',
                              })
                            }
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="committed">Committed</option>
                            <option value="aspirational">Aspirational</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Owner
                          </label>
                          <OwnerSelector
                            organizationId={selectedOrg?.id || ''}
                            selectedOwnerId={newKrData.owner_id}
                            onOwnerSelect={(ownerId) =>
                              setNewKrData({
                                ...newKrData,
                                owner_id: ownerId || undefined,
                              })
                            }
                            placeholder="Select"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setShowNewKrForm(false);
                            setNewKrData({
                              description: '',
                              target_value: 100,
                              unit: '%',
                              commitment_type: 'committed',
                            });
                          }}
                          className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddNewKr}
                          disabled={savingKrId === 'new'}
                          className="px-3 py-1 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingKrId === 'new'
                            ? 'Adding...'
                            : 'Add Key Result'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Existing Key Results - Different rendering based on edit mode */}
                {editMode
                  ? // Full edit mode - existing behavior
                    keyResultsEdit
                      .filter((kr) => !deletedKeyResultIds.includes(kr.id))
                      .map((kr, index) => (
                        <div key={kr.id} className="bg-gray-50 rounded-lg p-4">
                          {
                            editMode ? (
                              <div className="space-y-3">
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={kr.description}
                                    onChange={(e) =>
                                      handleUpdateKeyResult(index, {
                                        description: e.target.value,
                                      })
                                    }
                                    className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                                    placeholder="Key result description"
                                  />
                                  <button
                                    onClick={() => handleRemoveKeyResult(kr)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </div>

                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-600">
                                      Current
                                    </label>
                                    <input
                                      type="number"
                                      value={kr.current_value}
                                      onChange={(e) =>
                                        handleUpdateKeyResult(index, {
                                          current_value: Number(e.target.value),
                                        })
                                      }
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600">
                                      Target
                                    </label>
                                    <input
                                      type="number"
                                      value={kr.target_value}
                                      onChange={(e) =>
                                        handleUpdateKeyResult(index, {
                                          target_value: Number(e.target.value),
                                        })
                                      }
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600">
                                      Unit
                                    </label>
                                    <input
                                      type="text"
                                      value={kr.unit}
                                      onChange={(e) =>
                                        handleUpdateKeyResult(index, {
                                          unit: e.target.value,
                                        })
                                      }
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                      placeholder="e.g., %"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">
                                    Owner (DRI)
                                  </label>
                                  <OwnerSelector
                                    organizationId={selectedOrg?.id || ''}
                                    selectedOwnerId={kr.owner_id}
                                    onOwnerSelect={(ownerId) =>
                                      handleUpdateKeyResult(index, {
                                        owner_id: ownerId || undefined,
                                      })
                                    }
                                    placeholder="Select owner for this Key Result"
                                  />
                                </div>
                              </div>
                            ) : null /* Will never reach here in full edit mode */
                          }
                        </div>
                      ))
                  : // Not in full edit mode - allow individual KR editing
                    okr.key_results
                      .sort(
                        (a, b) =>
                          (a.reference_number || 0) - (b.reference_number || 0),
                      )
                      .map((kr) => (
                        <div key={kr.id} className="bg-gray-50 rounded-lg p-4">
                          {editingKrId === kr.id ? (
                            // Individual KR editing
                            <div className="space-y-3">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={editingKrData?.description || ''}
                                  onChange={(e) =>
                                    setEditingKrData({
                                      ...editingKrData!,
                                      description: e.target.value,
                                    })
                                  }
                                  className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                                  placeholder="Key result description"
                                />
                              </div>

                              <div className="grid grid-cols-5 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-600">
                                    Current
                                  </label>
                                  <input
                                    type="number"
                                    value={editingKrData?.current_value || 0}
                                    onChange={(e) =>
                                      setEditingKrData({
                                        ...editingKrData!,
                                        current_value: Number(e.target.value),
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">
                                    Target
                                  </label>
                                  <input
                                    type="number"
                                    value={editingKrData?.target_value || 100}
                                    onChange={(e) =>
                                      setEditingKrData({
                                        ...editingKrData!,
                                        target_value: Number(e.target.value),
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    value={editingKrData?.unit || ''}
                                    onChange={(e) =>
                                      setEditingKrData({
                                        ...editingKrData!,
                                        unit: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    placeholder="%"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">
                                    Type
                                  </label>
                                  <select
                                    value={
                                      editingKrData?.commitment_type ||
                                      'committed'
                                    }
                                    onChange={(e) =>
                                      setEditingKrData({
                                        ...editingKrData!,
                                        commitment_type: e.target.value as
                                          | 'committed'
                                          | 'aspirational',
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  >
                                    {OKR_COMMITMENT_TYPES.map((type) => (
                                      <option
                                        key={type.value}
                                        value={type.value}
                                      >
                                        {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">
                                    Owner
                                  </label>
                                  <OwnerSelector
                                    organizationId={selectedOrg?.id || ''}
                                    selectedOwnerId={editingKrData?.owner_id}
                                    onOwnerSelect={(ownerId) =>
                                      setEditingKrData({
                                        ...editingKrData!,
                                        owner_id: ownerId || undefined,
                                      })
                                    }
                                    placeholder="Select"
                                  />
                                </div>
                              </div>

                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={handleCancelKrEdit}
                                  className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveKr}
                                  disabled={savingKrId === kr.id}
                                  className="px-3 py-1 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {savingKrId === kr.id ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View mode for individual KR
                            <div>
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {kr.reference_number &&
                                      `${kr.reference_number}. `}
                                    {kr.description}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        kr.commitment_type === 'aspirational'
                                          ? 'bg-purple-100 text-purple-800'
                                          : 'bg-blue-100 text-blue-800'
                                      }`}
                                    >
                                      {formatCommitmentType(kr.commitment_type)}
                                    </span>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getKRStatusColor(kr.status)}`}
                                    >
                                      {kr.status.replace('_', ' ')}
                                    </span>
                                    {kr.owner && (
                                      <span className="text-xs text-gray-500">
                                        DRI:{' '}
                                        {`${kr.owner.first_name || ''} ${kr.owner.last_name || ''}`.trim() ||
                                          kr.owner.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {canEdit && (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleEditKr(kr)}
                                      className="text-gray-600 hover:text-blue-600"
                                      title="Edit Key Result"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleCreateRelatedOKR(kr)}
                                      className="text-gray-600 hover:text-blue-600"
                                      title="Create related OKR"
                                    >
                                      <LinkIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteKr(kr.id)}
                                      className="text-gray-600 hover:text-red-600"
                                      title="Delete Key Result"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                      (kr.current_value / kr.target_value) *
                                        100 >=
                                      70
                                        ? 'bg-green-500'
                                        : (kr.current_value / kr.target_value) *
                                              100 >=
                                            40
                                          ? 'bg-yellow-500'
                                          : 'bg-red-500'
                                    }`}
                                    style={{
                                      width: `${Math.min(100, Math.max(0, (kr.current_value / kr.target_value) * 100))}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm text-gray-700">
                                  {kr.current_value}/{kr.target_value} {kr.unit}
                                </span>
                              </div>

                              {/* Tasks connected to this Key Result */}
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={() => toggleTasksExpansion(kr.id)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600"
                                  >
                                    <ClipboardDocumentListIcon className="h-4 w-4" />
                                    <span>
                                      {expandedKrTasks[kr.id] ? 'Hide' : 'Show'}{' '}
                                      Tasks
                                      {keyResultTasks[kr.id] &&
                                        ` (${keyResultTasks[kr.id].length})`}
                                    </span>
                                  </button>
                                  {canEdit && (
                                    <button
                                      onClick={() => {
                                        setSelectedKrId(kr.id);
                                        setShowTaskModal(true);
                                      }}
                                      className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                      title="Create task for this key result"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                      <span>Create Task</span>
                                    </button>
                                  )}
                                </div>

                                {expandedKrTasks[kr.id] && (
                                  <div className="mt-2 space-y-2">
                                    {loadingTasks[kr.id] ? (
                                      <div className="text-sm text-gray-500">
                                        Loading tasks...
                                      </div>
                                    ) : keyResultTasks[kr.id] &&
                                      keyResultTasks[kr.id].length > 0 ? (
                                      keyResultTasks[kr.id].map((task) => (
                                        <div
                                          key={task.id}
                                          className="p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                          onClick={() => handleTaskClick(task)}
                                        >
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-900">
                                                {task.title}
                                              </p>
                                              {task.description && (
                                                <p className="text-xs text-gray-600 mt-1">
                                                  {task.description}
                                                </p>
                                              )}
                                              <div className="flex items-center gap-3 mt-1">
                                                <span
                                                  className={`text-xs px-2 py-0.5 rounded-full ${
                                                    task.status === 'complete'
                                                      ? 'bg-green-100 text-green-800'
                                                      : task.status ===
                                                          'in_progress'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : task.status ===
                                                            'blocked'
                                                          ? 'bg-red-100 text-red-800'
                                                          : 'bg-gray-100 text-gray-800'
                                                  }`}
                                                >
                                                  {task.status.replace(
                                                    /_/g,
                                                    ' ',
                                                  )}
                                                </span>
                                                {task.assigned_to_name && (
                                                  <span className="text-xs text-gray-500">
                                                    {task.assigned_to_name}
                                                  </span>
                                                )}
                                                {task.due_date && (
                                                  <span className="text-xs text-gray-500">
                                                    Due:{' '}
                                                    {new Date(
                                                      task.due_date,
                                                    ).toLocaleDateString()}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-sm text-gray-500">
                                        No tasks connected to this key result
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                {/* New Key Results (in edit mode) */}
                {editMode &&
                  newKeyResults.map((kr, index) => (
                    <div
                      key={`new-${index}`}
                      className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200 border-dashed"
                    >
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={kr.description}
                            onChange={(e) =>
                              handleUpdateNewKeyResult(index, {
                                description: e.target.value,
                              })
                            }
                            className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                            placeholder="Key result description"
                          />
                          <button
                            onClick={() => handleRemoveNewKeyResult(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600">
                              Current
                            </label>
                            <input
                              type="number"
                              value={0}
                              disabled
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                              title="Current value starts at 0 for new Key Results"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600">
                              Target
                            </label>
                            <input
                              type="number"
                              value={kr.target_value}
                              onChange={(e) =>
                                handleUpdateNewKeyResult(index, {
                                  target_value: Number(e.target.value),
                                })
                              }
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600">
                              Unit
                            </label>
                            <input
                              type="text"
                              value={kr.unit}
                              onChange={(e) =>
                                handleUpdateNewKeyResult(index, {
                                  unit: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="e.g., %"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Owner (DRI)
                          </label>
                          <OwnerSelector
                            organizationId={selectedOrg?.id || ''}
                            selectedOwnerId={kr.owner_id}
                            onOwnerSelect={(ownerId) =>
                              handleUpdateNewKeyResult(index, {
                                owner_id: ownerId || undefined,
                              })
                            }
                            placeholder="Select owner for this Key Result"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                {((editMode &&
                  keyResultsEdit.filter(
                    (kr) => !deletedKeyResultIds.includes(kr.id),
                  ).length === 0 &&
                  newKeyResults.length === 0) ||
                  (!editMode && okr.key_results.length === 0)) && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No key results defined yet.
                    {(editMode || canEdit) &&
                      ' Click "Add Key Result" to create one.'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right column - Scoring and metadata */}
          <div className="space-y-6">
            {/* Score */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Score</h2>
              <OKRScoreDisplay
                progress={okr.progress_percentage || 0}
                score={okr.score}
                commitmentType={okr.commitment_type}
                showDetailed={true}
              />
            </div>

            {/* All Relationships Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Relationships</h2>
                <div className="flex space-x-2">
                  {canEdit && !editMode && (
                    <>
                      <button
                        onClick={() =>
                          navigate(`/okrs?create=true&parent_id=${id}`)
                        }
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Child
                      </button>
                      <button
                        onClick={() => setShowRelationshipModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Relationship
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Parent Objective */}
                {(parentOkr || canEdit) && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-sm font-medium text-gray-700">
                        Parent Objective
                      </h3>
                      {canEdit && !editMode && (
                        <button
                          onClick={async () => {
                            if (
                              !showParentSelector &&
                              availableParents.length === 0
                            ) {
                              // Load available parent OKRs
                              try {
                                const potentials =
                                  await okrRelationshipsService.getPotentialTargets(
                                    selectedOrg!.id,
                                    id,
                                  );
                                // Filter to only show corporate OKRs or OKRs that could be parents
                                const parents = potentials.filter(
                                  (o) => o.is_corporate || o.id !== id,
                                );
                                setAvailableParents(parents);
                              } catch (err) {
                                console.error(
                                  'Failed to load potential parents:',
                                  err,
                                );
                              }
                            }
                            setShowParentSelector(!showParentSelector);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          {parentOkr ? 'Change' : 'Set Parent'}
                        </button>
                      )}
                    </div>

                    {parentOkr ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => navigate(`/okrs/${parentOkr.id}`)}
                          className="text-left w-full p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          <p className="font-medium text-gray-900">
                            {parentOkr.formatted_reference &&
                              `${parentOkr.formatted_reference} - `}
                            {parentOkr.objective}
                          </p>
                          {parentOkr.is_corporate && (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Corporate
                            </span>
                          )}
                        </button>
                        {canEdit && !editMode && (
                          <button
                            onClick={async () => {
                              if (
                                window.confirm('Remove parent relationship?')
                              ) {
                                try {
                                  await okrRelationshipsService.setParent(
                                    id!,
                                    null,
                                    selectedOrg!.id,
                                  );
                                  setParentOkr(null);
                                  await loadOKR();
                                } catch (err) {
                                  console.error(
                                    'Failed to remove parent:',
                                    err,
                                  );
                                  alert('Failed to remove parent relationship');
                                }
                              }
                            }}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Remove Parent
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No parent objective set
                      </p>
                    )}

                    {/* Parent Selector */}
                    {showParentSelector && (
                      <div className="mt-2 p-3 border border-gray-200 rounded-lg">
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Select Parent Objective
                        </label>
                        <select
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={parentOkr?.id || ''}
                          onChange={async (e) => {
                            const parentId = e.target.value || null;
                            try {
                              await okrRelationshipsService.setParent(
                                id!,
                                parentId,
                                selectedOrg!.id,
                              );
                              await loadOKR();
                              setShowParentSelector(false);
                            } catch (err) {
                              console.error('Failed to set parent:', err);
                              alert('Failed to set parent relationship');
                            }
                          }}
                        >
                          <option value="">No Parent</option>
                          {availableParents.map((parent) => (
                            <option key={parent.id} value={parent.id}>
                              {parent.formatted_reference &&
                                `${parent.formatted_reference} - `}
                              {parent.objective}
                              {parent.is_corporate && ' (Corporate)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Child Objectives */}
                {okr.child_okrs && okr.child_okrs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Child Objectives
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({okr.child_okrs.length})
                      </span>
                    </h3>
                    <div className="space-y-2">
                      {/* Show first 5 children, with option to expand */}
                      {okr.child_okrs
                        .slice(0, showAllChildren ? undefined : 5)
                        .map((child) => (
                          <button
                            key={child.id}
                            onClick={() => navigate(`/okrs/${child.id}`)}
                            className="text-left w-full p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {child.formatted_reference &&
                                    `${child.formatted_reference} - `}
                                  {child.objective}
                                </p>
                                {child.description && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                    {child.description}
                                  </p>
                                )}
                              </div>
                              <div className="ml-4 flex items-center space-x-2">
                                {/* Progress indicator */}
                                {child.progress_percentage !== undefined && (
                                  <div className="flex items-center">
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full ${
                                          child.progress_percentage >= 70
                                            ? 'bg-green-500'
                                            : child.progress_percentage >= 40
                                              ? 'bg-yellow-500'
                                              : 'bg-red-500'
                                        }`}
                                        style={{
                                          width: `${Math.min(100, Math.max(0, child.progress_percentage))}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="ml-2 text-xs text-gray-600">
                                      {Math.round(child.progress_percentage)}%
                                    </span>
                                  </div>
                                )}
                                {/* Status badge */}
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-medium ${
                                    child.status === 'completed'
                                      ? 'bg-blue-100 text-blue-800'
                                      : child.status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : child.status === 'cancelled'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {child.status}
                                </span>
                              </div>
                            </div>
                            {/* Owner info */}
                            {child.owner && (
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <UserCircleIcon className="h-4 w-4 mr-1" />
                                {child.owner.first_name || child.owner.last_name
                                  ? `${child.owner.first_name || ''} ${child.owner.last_name || ''}`.trim()
                                  : child.owner.email}
                              </div>
                            )}
                          </button>
                        ))}

                      {/* Show more button if there are more than 5 children */}
                      {okr.child_okrs.length > 5 && !showAllChildren && (
                        <button
                          onClick={() => setShowAllChildren(true)}
                          className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Show all {okr.child_okrs.length} child objectives
                        </button>
                      )}

                      {/* Show less button when expanded */}
                      {okr.child_okrs.length > 5 && showAllChildren && (
                        <button
                          onClick={() => setShowAllChildren(false)}
                          className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Show less
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Cross-Functional Relationships */}
                {okr.relationships && okr.relationships.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Cross-Functional Relationships
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({okr.relationships.length})
                      </span>
                    </h3>
                    <div className="space-y-4">
                      {/* Group relationships by type */}
                      {(() => {
                        const grouped = {
                          contributes_to: [] as typeof okr.relationships,
                          depends_on: [] as typeof okr.relationships,
                          aligned_with: [] as typeof okr.relationships,
                        };

                        okr.relationships.forEach((rel) => {
                          const type = rel.relationship_type
                            .replace('okr_', '')
                            .replace('kr_', '') as keyof typeof grouped;
                          if (grouped[type]) {
                            grouped[type].push(rel);
                          }
                        });

                        return Object.entries(grouped).map(([type, rels]) => {
                          if (rels.length === 0) return null;

                          const typeLabels = {
                            contributes_to: 'Contributes To',
                            depends_on: 'Depends On',
                            aligned_with: 'Aligned With',
                          };

                          const typeColors = {
                            contributes_to: 'text-green-700 bg-green-50',
                            depends_on: 'text-orange-700 bg-orange-50',
                            aligned_with: 'text-blue-700 bg-blue-50',
                          };

                          return (
                            <div key={type}>
                              <h4
                                className={`text-xs font-medium mb-1 px-2 py-0.5 rounded inline-block ${typeColors[type as keyof typeof typeColors]}`}
                              >
                                {typeLabels[type as keyof typeof typeLabels]}
                              </h4>
                              <div className="space-y-2 ml-2">
                                {rels.map((rel) => (
                                  <div
                                    key={rel.id}
                                    className="flex items-start justify-between"
                                  >
                                    <button
                                      onClick={() => {
                                        if (rel.related_okr?.id) {
                                          navigate(
                                            `/okrs/${rel.related_okr.id}`,
                                          );
                                        }
                                      }}
                                      className="flex-1 text-left p-2 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                    >
                                      <p className="font-medium text-sm text-gray-900">
                                        {(rel.related_okr as any)
                                          ?.formatted_reference &&
                                          `${(rel.related_okr as any).formatted_reference} - `}
                                        {rel.related_okr?.objective ||
                                          'Unknown Objective'}
                                      </p>
                                      {rel.contribution_notes && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          {rel.contribution_notes}
                                        </p>
                                      )}
                                    </button>
                                    {canEdit && !editMode && (
                                      <button
                                        onClick={async () => {
                                          if (
                                            window.confirm(
                                              'Remove this relationship?',
                                            )
                                          ) {
                                            try {
                                              await okrRelationshipsService.deleteRelationship(
                                                okr.id,
                                                rel.id,
                                                selectedOrg!.id,
                                              );
                                              await loadOKR();
                                            } catch (err) {
                                              console.error(
                                                'Failed to remove relationship:',
                                                err,
                                              );
                                              alert(
                                                'Failed to remove relationship',
                                              );
                                            }
                                          }
                                        }}
                                        className="ml-2 p-1 text-red-600 hover:text-red-800"
                                        title="Remove relationship"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Show message if no relationships at all */}
                {!parentOkr &&
                  (!okr.child_okrs || okr.child_okrs.length === 0) &&
                  (!okr.relationships || okr.relationships.length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No relationships yet. Add parent, child, or
                      cross-functional relationships to connect this OKR with
                      others.
                    </p>
                  )}
              </div>
            </div>

            {/* Manual Score Override */}
            {canEdit && !editMode && (
              <div className="bg-white rounded-lg shadow p-6">
                <ManualScoreEditor
                  autoScore={okr.auto_score !== false}
                  currentScore={
                    okr.score !== null && okr.score !== undefined
                      ? okr.score * 100
                      : undefined
                  }
                  calculatedScore={okr.progress_percentage || 0}
                  scoreJustification={okr.score_justification}
                  commitmentType={okr.commitment_type || 'committed'}
                  onScoreChange={async (autoScore, score, justification) => {
                    const update: OKRUpdate = {
                      auto_score: autoScore,
                      score: autoScore
                        ? undefined
                        : score !== undefined
                          ? score / 100
                          : undefined,
                      score_justification: autoScore
                        ? undefined
                        : justification,
                    };
                    await okrService.updateOKR(okr.id, selectedOrg!.id, update);
                    await loadOKR();
                  }}
                  disabled={false}
                />
              </div>
            )}

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Details</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">
                    {new Date(okr.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>
                  <p className="font-medium">
                    {new Date(okr.updated_at).toLocaleDateString()}
                  </p>
                </div>
                {okr.planning_cycle_id &&
                  (() => {
                    const cycle = planningCycles.find(
                      (c) => c.id === okr.planning_cycle_id,
                    );
                    return cycle ? (
                      <div>
                        <span className="text-gray-500">Planning Cycle:</span>
                        <p className="font-medium">{cycle.name}</p>
                      </div>
                    ) : null;
                  })()}
              </div>
            </div>
          </div>
        </div>
      )}{' '}
      {/* End of Overview Tab */}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Tasks</h2>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setTaskViewMode(taskViewMode === 'kanban' ? 'list' : 'kanban')
                }
                className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                title={
                  taskViewMode === 'kanban'
                    ? 'Switch to List View'
                    : 'Switch to Kanban View'
                }
              >
                {taskViewMode === 'kanban' ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {taskViewMode === 'kanban' ? (
            <TaskKanbanView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="okr"
              entityId={id || ''}
              canEdit={canEdit}
              segments={[]}
              propositions={[]}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          ) : (
            <TaskListView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="okr"
              entityId={id || ''}
              canEdit={canEdit}
              segments={[]}
              propositions={[]}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          )}
        </div>
      )}
      {/* Research Tab */}
      {activeTab === 'research' && okr && selectedOrg && (
        <ResearchTab
          organizationId={selectedOrg.id}
          entityType={ResearchEntityType.OKR}
          entityId={okr.id}
          entityName={okr.objective}
          canEdit={canEdit}
        />
      )}
      {/* Add Relationship Modal */}
      {okr && (
        <AddRelationshipModal
          open={showRelationshipModal}
          onClose={() => setShowRelationshipModal(false)}
          okrId={okr.id}
          organizationId={selectedOrg?.id || ''}
          currentOkrObjective={okr.objective}
          onRelationshipAdded={loadOKR}
        />
      )}
      {/* Task Detail Panel */}
      <TaskDetailPanel
        task={selectedTask}
        isOpen={showTaskDetailPanel}
        onClose={() => {
          setShowTaskDetailPanel(false);
          setSelectedTask(null);
        }}
        onUpdate={async (updatedTask) => {
          // Find the original task to see if key_result_id changed
          const originalTask = selectedTask;

          // Normalize key_result_id (treat empty string, null, undefined as "no KR")
          const oldKrId = originalTask?.key_result_id || null;
          const newKrId = updatedTask.key_result_id || null;

          // If the task's key_result_id changed or was cleared, reload the affected KRs
          if (oldKrId !== newKrId) {
            // Remove from old KR if it had one
            if (oldKrId) {
              await loadTasksForKeyResult(oldKrId);
            }
            // Add to new KR if it has one
            if (newKrId) {
              await loadTasksForKeyResult(newKrId);
            }
          } else {
            // Same KR, just update in place
            handleKeyResultTaskUpdate(updatedTask.id!, updatedTask);
          }

          // ALWAYS refresh Tasks tab to keep taskboard in sync
          if (selectedOrg) {
            await fetchTasks(selectedOrg.id);
          }

          setSelectedTask(updatedTask);
        }}
        onDelete={async (taskId) => {
          if (!selectedOrg) return;

          try {
            // Actually delete the task from the backend
            await masterPlanTaskService.delete(selectedOrg.id, taskId);

            // Remove task from local state
            setKeyResultTasks((prev) => {
              const newTasks = { ...prev };
              Object.keys(newTasks).forEach((krId) => {
                newTasks[krId] = newTasks[krId].filter(
                  (task) => task.id !== taskId,
                );
              });
              return newTasks;
            });

            // Also refresh the Tasks tab if active
            if (activeTab === 'tasks') {
              await fetchTasks(selectedOrg.id);
            }

            setShowTaskDetailPanel(false);
            setSelectedTask(null);
          } catch (error) {
            console.error('Failed to delete task:', error);
          }
        }}
        organizationId={selectedOrg?.id || ''}
        currentUserId={user?.id || ''}
        canEdit={canEdit}
        isAdmin={canEdit}
        segments={[]} // These would need to be loaded if needed
        propositions={[]} // These would need to be loaded if needed
        groups={[]} // These would need to be loaded if needed
        positions={[]} // These would need to be loaded if needed
        companies={[]} // These would need to be loaded if needed
      />
      {/* Task Creation Modal for Key Results */}
      {showTaskModal && okr && selectedOrg && (
        <TaskModal
          task={null}
          organizationId={selectedOrg.id}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedKrId(null);
          }}
          onSave={handleKrTaskCreate}
          segments={[]}
          propositions={[]}
          groups={[]}
          positions={[]}
          companies={[]}
          isAdmin={canEdit}
          initialValues={{
            okr_id: okr.id,
            key_result_id: selectedKrId || undefined,
          }}
        />
      )}
    </div>
  );
};

export default OKRDetail;
